import React, { useState } from 'react';
import { Save, Bell, Clock, Users, Mail, MessageSquare, Settings, Shield, ChevronRight } from 'lucide-react';

interface NotificationChannel {
  id: string;
  name: string;
  type: 'email' | 'sms' | 'push' | 'in_app';
  enabled: boolean;
  description: string;
}

interface AutoNotificationRule {
  id: string;
  name: string;
  trigger: string;
  recipients: string[];
  template: string;
  enabled: boolean;
  description: string;
}

const NotificationSettings: React.FC = () => {
  const [channels, setChannels] = useState<NotificationChannel[]>([
    {
      id: '1',
      name: 'Email Notifications',
      type: 'email',
      enabled: true,
      description: 'Send notifications via email to parents and teachers'
    },
    {
      id: '2',
      name: 'SMS Notifications',
      type: 'sms',
      enabled: true,
      description: 'Send urgent notifications via SMS'
    },
    {
      id: '3',
      name: 'Push Notifications',
      type: 'push',
      enabled: false,
      description: 'Send push notifications to mobile app users'
    },
    {
      id: '4',
      name: 'In-App Notifications',
      type: 'in_app',
      enabled: true,
      description: 'Show notifications within the school management system'
    }
  ]);

  const [autoRules, setAutoRules] = useState<AutoNotificationRule[]>([
    {
      id: '1',
      name: 'Student Absence Alert',
      trigger: 'Student marked absent',
      recipients: ['parents'],
      template: 'absence_alert',
      enabled: true,
      description: 'Automatically notify parents when their child is marked absent'
    },
    {
      id: '2',
      name: 'Fee Due Reminder',
      trigger: 'Fee due in 7 days',
      recipients: ['parents'],
      template: 'fee_reminder',
      enabled: true,
      description: 'Send automatic fee reminders 7 days before due date'
    },
    {
      id: '3',
      name: 'Exam Schedule Published',
      trigger: 'Exam schedule created',
      recipients: ['students', 'parents'],
      template: 'exam_schedule',
      enabled: false,
      description: 'Notify when exam schedules are published'
    },
    {
      id: '4',
      name: 'Holiday Announcement',
      trigger: 'Holiday declared',
      recipients: ['all'],
      template: 'holiday_notice',
      enabled: true,
      description: 'Automatically announce school holidays'
    }
  ]);

  const [generalSettings, setGeneralSettings] = useState({
    defaultSender: 'admin@school.edu',
    maxDailyNotifications: 50,
    quietHoursStart: '22:00',
    quietHoursEnd: '07:00',
    enableDeliveryTracking: true,
    enableReadReceipts: false,
    retryFailedNotifications: true,
    defaultPriority: 'medium' as 'low' | 'medium' | 'high' | 'urgent'
  });

  const toggleChannel = (channelId: string) => {
    setChannels(channels.map(channel =>
      channel.id === channelId
        ? { ...channel, enabled: !channel.enabled }
        : channel
    ));
  };

  const toggleAutoRule = (ruleId: string) => {
    setAutoRules(autoRules.map(rule =>
      rule.id === ruleId
        ? { ...rule, enabled: !rule.enabled }
        : rule
    ));
  };

  const handleGeneralSettingsChange = (field: string, value: any) => {
    setGeneralSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveSettings = () => {
    console.log('Saving notification settings...', {
      channels,
      autoRules,
      generalSettings
    });
    alert('Settings saved successfully!');
  };

  const getChannelIcon = (type: string) => {
    switch (type) {
      case 'email':
        return Mail;
      case 'sms':
        return MessageSquare;
      case 'push':
        return Bell;
      case 'in_app':
        return Settings;
      default:
        return Bell;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-[#f3ede2]">Notification Settings</h2>
          <p className="text-[#b9ad9b] mt-1">Configure notification channels, automation, and preferences</p>
        </div>
        <button
          onClick={handleSaveSettings}
          className="flex items-center space-x-2 bg-[#e2916d] text-white px-4 py-2 rounded-lg hover:bg-[#f0a67d] transition-colors"
        >
          <Save className="w-4 h-4" />
          <span>Save Settings</span>
        </button>
      </div>

      {/* Notification Channels */}
      <div className="bg-[#211c15] rounded-xl border border-[#3a3122] p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-8 h-8 bg-[#16212b] rounded-lg flex items-center justify-center">
            <Bell className="w-4 h-4 text-[#60a5fa]" />
          </div>
          <div>
            <h3 className="text-lg font-medium text-[#f3ede2]">Notification Channels</h3>
            <p className="text-[#b9ad9b] text-sm">Enable or disable different notification delivery methods</p>
          </div>
        </div>

        <div className="space-y-4">
          {channels.map((channel) => {
            const Icon = getChannelIcon(channel.type);
            return (
              <div key={channel.id} className="flex items-center justify-between p-4 border border-[#3a3122] rounded-lg">
                <div className="flex items-center space-x-3">
                  <Icon className="w-5 h-5 text-[#b9ad9b]" />
                  <div>
                    <h4 className="font-medium text-[#f3ede2]">{channel.name}</h4>
                    <p className="text-[#8a7f6e] text-sm">{channel.description}</p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={channel.enabled}
                    onChange={() => toggleChannel(channel.id)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-[#3a3122] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#e2916d]"></div>
                </label>
              </div>
            );
          })}
        </div>
      </div>

      {/* Auto Notification Rules */}
      <div className="bg-[#211c15] rounded-xl border border-[#3a3122] p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-8 h-8 bg-[#173322] rounded-lg flex items-center justify-center">
            <Clock className="w-4 h-4 text-[#4ade80]" />
          </div>
          <div>
            <h3 className="text-lg font-medium text-[#f3ede2]">Automatic Notifications</h3>
            <p className="text-[#b9ad9b] text-sm">Configure rules for automatic notification triggers</p>
          </div>
        </div>

        <div className="space-y-4">
          {autoRules.map((rule) => (
            <div key={rule.id} className="flex items-center justify-between p-4 border border-[#3a3122] rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-[#332711] rounded-lg flex items-center justify-center">
                  <ChevronRight className="w-4 h-4 text-[#f0b64c]" />
                </div>
                <div>
                  <h4 className="font-medium text-[#f3ede2]">{rule.name}</h4>
                  <p className="text-[#8a7f6e] text-sm">{rule.description}</p>
                  <div className="flex items-center space-x-4 mt-1">
                    <span className="text-xs text-[#b9ad9b]">Trigger: {rule.trigger}</span>
                    <span className="text-xs text-[#b9ad9b]">Recipients: {rule.recipients.join(', ')}</span>
                  </div>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={rule.enabled}
                  onChange={() => toggleAutoRule(rule.id)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-[#3a3122] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#e2916d]"></div>
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* General Settings */}
      <div className="bg-[#211c15] rounded-xl border border-[#3a3122] p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-8 h-8 bg-[#332217] rounded-lg flex items-center justify-center">
            <Shield className="w-4 h-4 text-[#f0a67d]" />
          </div>
          <div>
            <h3 className="text-lg font-medium text-[#f3ede2]">General Settings</h3>
            <p className="text-[#b9ad9b] text-sm">Configure general notification preferences and limits</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-[#f3ede2] mb-2">
              Default Sender Email
            </label>
            <input
              type="email"
              value={generalSettings.defaultSender}
              onChange={(e) => handleGeneralSettingsChange('defaultSender', e.target.value)}
              className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#f3ede2] mb-2">
              Max Daily Notifications per User
            </label>
            <input
              type="number"
              value={generalSettings.maxDailyNotifications}
              onChange={(e) => handleGeneralSettingsChange('maxDailyNotifications', parseInt(e.target.value))}
              className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#f3ede2] mb-2">
              Quiet Hours Start
            </label>
            <input
              type="time"
              value={generalSettings.quietHoursStart}
              onChange={(e) => handleGeneralSettingsChange('quietHoursStart', e.target.value)}
              className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#f3ede2] mb-2">
              Quiet Hours End
            </label>
            <input
              type="time"
              value={generalSettings.quietHoursEnd}
              onChange={(e) => handleGeneralSettingsChange('quietHoursEnd', e.target.value)}
              className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#f3ede2] mb-2">
              Default Priority
            </label>
            <select
              value={generalSettings.defaultPriority}
              onChange={(e) => handleGeneralSettingsChange('defaultPriority', e.target.value)}
              className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
        </div>

        <div className="mt-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-[#f3ede2]">Enable Delivery Tracking</h4>
              <p className="text-[#8a7f6e] text-sm">Track when notifications are delivered and opened</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={generalSettings.enableDeliveryTracking}
                onChange={(e) => handleGeneralSettingsChange('enableDeliveryTracking', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-[#3a3122] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#e2916d]"></div>
            </label>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-[#f3ede2]">Enable Read Receipts</h4>
              <p className="text-[#8a7f6e] text-sm">Request read receipts for important notifications</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={generalSettings.enableReadReceipts}
                onChange={(e) => handleGeneralSettingsChange('enableReadReceipts', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-[#3a3122] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#e2916d]"></div>
            </label>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-[#f3ede2]">Retry Failed Notifications</h4>
              <p className="text-[#8a7f6e] text-sm">Automatically retry failed notification deliveries</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={generalSettings.retryFailedNotifications}
                onChange={(e) => handleGeneralSettingsChange('retryFailedNotifications', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-[#3a3122] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#e2916d]"></div>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;